import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useQuery } from '@tanstack/react-query'
import { USER_QUERY_KEYS } from '@/api/queryKeys'
import { getRoles } from '@/api/userApi'
import { MultiSelect } from './Multiselect'
import {
  transformCategoriesToOptions,
  transformRolesToOptions,
} from '@/utils/helper'
import { DatePicker } from './ui/date-picker'
import { UserSelect, type UserSelectValue } from './UserSelect'
import { USER_ROLES, USER_STATUSES } from '@/constants'
import { AdvertisementStatusMap } from '@/constants/sponsor'

interface FilterDrawerProps {
  open: boolean
  registerLabel?: boolean
  onOpenChange: (open: boolean) => void
  onApplyFilters: (filters: FilterValues) => void
  onSponsorsChange?: (sponsors: UserSelectValue[]) => void
  fields: Array<
    | 'userRole'
    | 'subscriptionType'
    | 'eventAttendance'
    | 'registrationStartDate'
    | 'registrationEndDate'
    | 'status'
    | 'statusEvent'
    | 'category'
    | 'purchaseDate'
    | 'ticketType'
    | 'statusAttendee'
    | 'placement'
    | 'bySponsor'
    | 'adStatus'
  >
  fieldOptions?: Partial<{
    userRole: Array<{ id: string; name: string; title: string }>
    category: Array<{ id: string; name: string }>
    subscriptionType: string[]
    eventAttendance: string[]
    status: string[]
    placement?: string[]
    bySponsor?: string[]
    adStatus?: string[]
  }>
  appliedFilter: FilterValues
  buttonText?: string
  isDisableFutureDates?: boolean
}

export interface FilterValues {
  userRole: string[]
  subscriptionType: string
  eventAttendance: string
  registrationStartDate: string
  registrationEndDate: string
  status: string[]
  statusEvent: string
  category: string[]
  purchaseDate: string
  placement?: string
  eventId?: string
  bySponsor?: string[]
  adStatus?: string[]
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onOpenChange,
  onApplyFilters,
  onSponsorsChange,
  fields,
  fieldOptions = {},
  registerLabel = false,
  appliedFilter,
  buttonText,
  isDisableFutureDates = false,
}) => {
  const [filters, setFilters] = React.useState<FilterValues>({
    userRole: [],
    subscriptionType: '',
    eventAttendance: '',
    registrationStartDate: '',
    registrationEndDate: '',
    status: [],
    statusEvent: '',
    category: [],
    purchaseDate: '',
    placement: '',
    eventId: '',
    bySponsor: [],
    adStatus: [],
  })
  const [ticketSelectValue, setTicketSelectValue] = useState('')
  const [selectedSponsor, setSelectedSponsor] = useState<UserSelectValue[]>([])

  useEffect(() => {
    setFilters({
      userRole: appliedFilter.userRole || [],
      subscriptionType: appliedFilter.subscriptionType || '',
      eventAttendance: appliedFilter.eventAttendance || '',
      registrationStartDate: appliedFilter.registrationStartDate || '',
      registrationEndDate: appliedFilter.registrationEndDate || '',
      status: appliedFilter.status || '',
      statusEvent: appliedFilter.statusEvent || '',
      category: appliedFilter.category || '',
      purchaseDate: appliedFilter.purchaseDate || '',
      placement: appliedFilter.placement || '',
      eventId: appliedFilter.eventId || '',
      bySponsor: appliedFilter.bySponsor || [],
      adStatus: appliedFilter.adStatus || [],
    })

    // Sync selectedSponsor state with appliedFilter.bySponsor
    const appliedSponsorIds = appliedFilter.bySponsor || []
    if (appliedSponsorIds.length === 0) {
      // If no sponsors in applied filter, clear selectedSponsor
      setSelectedSponsor([])
    } else {
      // Filter selectedSponsor to only include sponsors that are still in appliedFilter
      setSelectedSponsor(prev =>
        prev.filter(sponsor => appliedSponsorIds.includes(sponsor.id))
      )
    }
  }, [appliedFilter])

  // const [date, setDate] = React.useState<Date>()
  // Fetch roles data
  const { data: rolesData } = useQuery({
    queryKey: USER_QUERY_KEYS.roles(),
    queryFn: getRoles,
  })
  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const handleReset = () => {
    setFilters({
      userRole: [],
      subscriptionType: '',
      eventAttendance: '',
      registrationStartDate: '',
      registrationEndDate: '',
      status: [],
      statusEvent: '',
      category: [],
      purchaseDate: '',
      placement: '',
      bySponsor: [],
      adStatus: [],
    })
    // Also clear selectedSponsor when resetting filters
    setSelectedSponsor([])
  }

  const handleApply = () => {
    onSponsorsChange?.(selectedSponsor)
    onApplyFilters(filters)
    onOpenChange(false)
  }

  // Update hasFilters logic to properly handle arrays
  const startDate = filters.registrationStartDate
    ? parseISO(filters.registrationStartDate)
    : undefined

  const endDate = filters.registrationEndDate
    ? parseISO(filters.registrationEndDate)
    : undefined

  const purchaseDate = filters.purchaseDate
    ? parseISO(filters.purchaseDate)
    : undefined

  const handleDateChange =
    (field: 'registrationStartDate' | 'registrationEndDate' | 'purchaseDate') =>
    (picked: Date | undefined) => {
      setFilters(prev => ({
        ...prev,
        [field]: picked ? format(picked, 'yyyy-MM-dd') : '',
      }))
    }

  const areFiltersEqual = (filters1: FilterValues, filters2: FilterValues) => {
    const arrayFields: (keyof FilterValues)[] = [
      'userRole',
      'status',
      'category',
      'adStatus',
      'bySponsor',
    ]
    for (const field of arrayFields) {
      const arr1 = Array.isArray(filters1[field])
        ? (filters1[field] as string[]).sort((a, b) => a.localeCompare(b))
        : []
      const arr2 = Array.isArray(filters2[field])
        ? (filters2[field] as string[]).sort((a, b) => a.localeCompare(b))
        : []
      if (JSON.stringify(arr1) !== JSON.stringify(arr2)) {
        return false
      }
    }

    // Compare string fields
    const stringFields: (keyof FilterValues)[] = [
      'subscriptionType',
      'eventAttendance',
      'registrationStartDate',
      'registrationEndDate',
      'statusEvent',
      'purchaseDate',
      'placement',
    ]

    for (const field of stringFields) {
      if ((filters1[field] || '') !== (filters2[field] || '')) {
        return false
      }
    }

    return true
  }
  const hasFilters = React.useMemo(() => {
    return !areFiltersEqual(filters, appliedFilter)
  }, [filters, appliedFilter])

  const hasActiveFilters = React.useMemo(() => {
    return (
      filters.userRole.length > 0 ||
      filters.subscriptionType !== '' ||
      filters.eventAttendance !== '' ||
      filters.registrationStartDate !== '' ||
      filters.registrationEndDate !== '' ||
      filters.status.length > 0 ||
      filters.statusEvent !== '' ||
      filters.category.length > 0 ||
      filters.purchaseDate !== '' ||
      filters.placement !== '' ||
      (filters.bySponsor?.length ?? 0) > 0 ||
      (filters.adStatus?.length ?? 0) > 0
    )
  }, [filters])

  const roleOptions = transformRolesToOptions(rolesData)
  const sponserRoleOption = roleOptions.find(
    val => val.name === USER_ROLES.sponsor
  )

  const handleSponsorSelectValueChange = (
    users: UserSelectValue | UserSelectValue[] | null
  ) => {
    if (Array.isArray(users)) {
      setSelectedSponsor(users)
      setFilters(prev => ({
        ...prev,
        bySponsor: users.map(user => user.id),
      }))
    } else if (users) {
      setSelectedSponsor([users])
      setFilters(prev => ({
        ...prev,
        bySponsor: [users.id],
      }))
    } else {
      setSelectedSponsor([])
      setFilters(prev => ({
        ...prev,
        bySponsor: [],
      }))
    }
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction="right"
      aria-label="Filter drawer"
    >
      <DrawerContent className="w-full sm:w-[400px] h-full bg-ss-neutral-white border-l border-ss-neutral-50 rounded-none md:rounded-xl">
        {/* Header */}
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-ss-neutral-50">
          <DrawerTitle
            className="text-lg font-semibold text-ss-neutral-950"
            id="filter-drawer-title"
          >
            Filters
          </DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-ss-action-gray-50"
              aria-label="Close filter drawer"
            >
              <X className="h-5 w-5 text-ss-neutral-600" aria-hidden="true" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {/* Filter Fields */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {fields.includes('userRole') && (
            <div className="space-y-2">
              <MultiSelect
                label="Role"
                options={
                  fieldOptions.userRole ||
                  transformRolesToOptions(rolesData) ||
                  []
                }
                selected={filters.userRole}
                onChange={selectedIds => {
                  setFilters(prev => ({ ...prev, userRole: selectedIds }))
                }}
                placeholder="Select roles..."
              />
            </div>
          )}

          {fields.includes('eventAttendance') && (
            <div className="space-y-2">
              <Label
                className="text-sm font-medium text-ss-neutral-950"
                htmlFor="event-attendance-select"
              >
                Event Attendance
              </Label>
              <Select
                value={filters.eventAttendance}
                onValueChange={value =>
                  handleFilterChange('eventAttendance', value)
                }
              >
                <SelectTrigger
                  id="event-attendance-select"
                  className="w-full h-11 border-ss-neutral-200 bg-ss-neutral-white hover:border-ss-blue-700 focus:border-ss-blue-700"
                  aria-label="Select event attendance"
                >
                  <SelectValue placeholder="Select number" />
                </SelectTrigger>
                <SelectContent>
                  {(
                    fieldOptions.eventAttendance || [
                      '0-5 events',
                      '6-10 events',
                      '11-15 events',
                      '16+ events',
                    ]
                  ).map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {fields.includes('purchaseDate') && (
            <div className="flex-1 relative">
              <Label
                className="text-sm font-medium text-ss-neutral-600 mb-2"
                htmlFor="registration-end-date-button"
              >
                Purchase Date
              </Label>
              <DatePicker
                className="h-9"
                value={purchaseDate}
                onChange={handleDateChange('purchaseDate')}
                placeholder="Select Purchase date"
                id="purchase-date-button"
                aria-label="Select purchase date"
                disableFutureDates={true}
                fromYear={1940}
                toYear={new Date().getFullYear()}
              />
            </div>
          )}

          {fields.includes('placement') && (
            <div className="space-y-2">
              <Label
                className="text-sm font-medium text-ss-neutral-950"
                htmlFor="placement-select"
              >
                Placement
              </Label>
              <Select
                value={filters.placement}
                onValueChange={value => handleFilterChange('placement', value)}
              >
                <SelectTrigger
                  id="placement-select"
                  className="w-full h-11 border-ss-neutral-200 bg-ss-neutral-white hover:border-ss-blue-700 focus:border-ss-blue-700"
                  aria-label="Select ad placement"
                >
                  <SelectValue placeholder="Select placement" />
                </SelectTrigger>
                <SelectContent>
                  {(
                    fieldOptions.placement || [
                      'Hero sponsor',
                      'In-feed native card',
                      'Right-rail display',
                      'Mobile sticky footer',
                      'Event detail',
                    ]
                  ).map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {fields.includes('bySponsor') && (
            <div className="space-y-2">
              <UserSelect
                label="By Sponsor"
                value={selectedSponsor}
                roleFilter={sponserRoleOption ? [sponserRoleOption.id] : []}
                statusFilter={[USER_STATUSES.active]}
                onValueChange={handleSponsorSelectValueChange}
                placeholder="Select sponsors..."
                multiple={true}
                className="h-9"
              />
            </div>
          )}

          {fields.includes('adStatus') && (
            <div className="space-y-2">
              <MultiSelect
                label="Ad Status"
                options={Object.entries(AdvertisementStatusMap).map(
                  ([title, id]) => ({ id, name: id, title })
                )}
                selected={filters.adStatus || []}
                onChange={selectedIds =>
                  setFilters(prev => ({ ...prev, adStatus: selectedIds }))
                }
                placeholder="Select ad status..."
              />
            </div>
          )}

          {(fields.includes('registrationStartDate') ||
            fields.includes('registrationEndDate')) && (
            <div>
              {registerLabel && (
                <Label
                  className="text-sm font-medium text-ss-neutral-950 mb-2"
                  htmlFor="registration-start-date-button"
                >
                  Registration Date
                </Label>
              )}
              <div className="flex gap-2">
                {fields.includes('registrationStartDate') && (
                  <div className="flex-1 relative">
                    <Label
                      className="text-sm font-medium text-ss-neutral-600"
                      htmlFor="registration-start-date-button"
                    >
                      Start Date
                    </Label>
                    <DatePicker
                      className="h-9"
                      value={startDate}
                      onChange={handleDateChange('registrationStartDate')}
                      placeholder="Select start date"
                      id="registration-start-date-button"
                      aria-label="Select start date"
                      fromYear={1940}
                      toYear={new Date().getFullYear()}
                      disableFutureDates={isDisableFutureDates}
                    />
                  </div>
                )}
                {fields.includes('registrationEndDate') && (
                  <div className="flex-1 relative">
                    <Label
                      className="text-sm font-medium text-ss-neutral-600"
                      htmlFor="registration-end-date-button"
                    >
                      End Date
                    </Label>
                    <DatePicker
                      className="h-9"
                      value={endDate}
                      onChange={handleDateChange('registrationEndDate')}
                      placeholder="Select end date"
                      id="registration-end-date-button"
                      aria-label="Select end date"
                      fromYear={1940}
                      toYear={new Date().getFullYear()}
                      disableFutureDates={isDisableFutureDates}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {fields.includes('category') && (
            <div className="space-y-2">
              <MultiSelect
                label="Category"
                options={
                  transformCategoriesToOptions(fieldOptions.category) || []
                }
                selected={filters.category}
                onChange={selectedIds =>
                  setFilters(prev => ({ ...prev, category: selectedIds }))
                }
                placeholder="Select categories..."
              />
            </div>
          )}

          {fields.includes('statusEvent') && (
            <div className="space-y-2">
              <MultiSelect
                label="Status"
                options={(
                  fieldOptions.status || ['Active', 'Suspended', 'Banned']
                ).map(status => ({
                  id: status.toLowerCase(),
                  name: status.toLowerCase(),
                  title: status,
                }))}
                selected={filters.status}
                onChange={selectedIds =>
                  setFilters(prev => ({ ...prev, status: selectedIds }))
                }
                placeholder="Select status..."
              />
            </div>
          )}

          {fields.includes('statusAttendee') && (
            <div className="space-y-2">
              <MultiSelect
                label="Status"
                options={(
                  fieldOptions.status || ['Active', 'Suspended', 'Banned']
                ).map(status => ({
                  id: status.toLowerCase(),
                  name: status.toLowerCase(),
                  title: status,
                }))}
                selected={filters.status}
                onChange={selectedIds =>
                  setFilters(prev => ({ ...prev, status: selectedIds }))
                }
                placeholder="Select status..."
              />
            </div>
          )}

          {fields.includes('status') && (
            <div className="space-y-2">
              <MultiSelect
                label="Status"
                options={(
                  fieldOptions.status || ['Active', 'Suspended', 'Banned']
                ).map(status => ({
                  id: status.toLowerCase(),
                  name: status.toLowerCase(),
                  title: status,
                }))}
                selected={filters.status}
                onChange={selectedIds =>
                  setFilters(prev => ({ ...prev, status: selectedIds }))
                }
                placeholder="Select status..."
              />
            </div>
          )}
          {fields.includes('ticketType') && (
            <div className="flex-1 relative">
              <Label
                className="text-sm font-medium text-ss-neutral-600 mb-2"
                htmlFor="registration-end-date-button"
              >
                Ticket Type
              </Label>
              <Select
                value={ticketSelectValue}
                onValueChange={setTicketSelectValue}
              >
                <SelectTrigger
                  id="event-attendance-select"
                  className="w-full h-11 border-ss-neutral-200 bg-transparent hover:border-ss-blue-700 focus:border-ss-blue-700"
                  aria-label="Select event attendance"
                >
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent className=" truncate">
                  {[
                    'General Admission',
                    'VIP Admission',
                    'Backstage Pass',
                    'Group Ticket',
                    'Early Bird Ticket',
                  ].map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Footer with buttons */}
        <DrawerFooter className="p-4 border-t border-ss-neutral-50 bg-ss-neutral-white shadow-lg">
          <div className="flex gap-3 w-full justify-between">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={!hasActiveFilters}
              className="w-fit h-9 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-action-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Reset all filters"
              tabIndex={0}
            >
              Reset
            </Button>
            <Button
              onClick={handleApply}
              disabled={!hasFilters}
              className="w-fit h-9 bg-ss-blue-900 text-ss-neutral-white hover:bg-ss-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Apply filters"
              tabIndex={0}
            >
              {buttonText || 'Apply Now'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default FilterDrawer
